import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloseIcon from '@mui/icons-material/Close';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import React, { useRef } from 'react';

import { type AttachmentFile } from '../types';

interface AttachmentsSectionProps {
  autoScreenshotUrl: string | null;
  isCapturing: boolean;
  extraFiles: AttachmentFile[];
  onRemoveAutoScreenshot: () => void;
  onAddFiles: (files: File[]) => void;
  onRemoveExtraFile: (index: number) => void;
  disabled?: boolean;
}

export const AttachmentsSection: React.FC<AttachmentsSectionProps> = ({
  autoScreenshotUrl,
  isCapturing,
  extraFiles,
  onRemoveAutoScreenshot,
  onAddFiles,
  onRemoveExtraFile,
  disabled,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length) {
      onAddFiles(files);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 0.5 }}>
        Screenshots or video <span style={{ fontWeight: 400, color: '#6b7280' }}>(optional)</span>
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {/* Auto screenshot thumbnail */}
        {isCapturing ? (
          <Box
            sx={{
              width: 80,
              height: 64,
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              backgroundColor: '#f3f4f6',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <CircularProgress size={18} />
          </Box>
        ) : autoScreenshotUrl ? (
          <Box
            sx={{
              position: 'relative',
              width: 80,
              height: 64,
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
              backgroundColor: '#f3f4f6',
              '&:hover .remove-btn': { opacity: 1 },
            }}
          >
            <Box
              component="img"
              src={autoScreenshotUrl}
              alt="Auto screenshot"
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 3,
                left: 3,
                px: 0.6,
                py: 0.1,
                borderRadius: '4px',
                backgroundColor: 'rgba(0, 0, 0, 0.65)',
                color: '#ffffff',
                fontSize: '9px',
                fontWeight: 600,
              }}
            >
              auto
            </Box>
            <IconButton
              className="remove-btn"
              size="small"
              onClick={onRemoveAutoScreenshot}
              disabled={disabled}
              sx={{
                position: 'absolute',
                top: 2,
                right: 2,
                width: 18,
                height: 18,
                backgroundColor: 'rgba(0, 0, 0, 0.65)',
                color: '#ffffff',
                opacity: 0,
                transition: 'opacity 0.2s',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.85)' },
              }}
            >
              <CloseIcon sx={{ fontSize: 12 }} />
            </IconButton>
          </Box>
        ) : null}

        {/* Extra file attachments */}
        {extraFiles.map((att, idx) => (
          <Box
            key={idx}
            sx={{
              position: 'relative',
              width: 80,
              height: 64,
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
              backgroundColor: '#f3f4f6',
              '&:hover .remove-btn': { opacity: 1 },
            }}
          >
            <Box
              component="img"
              src={att.previewUrl}
              alt="Uploaded attachment"
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            <IconButton
              className="remove-btn"
              size="small"
              onClick={() => onRemoveExtraFile(idx)}
              disabled={disabled}
              sx={{
                position: 'absolute',
                top: 2,
                right: 2,
                width: 18,
                height: 18,
                backgroundColor: 'rgba(0, 0, 0, 0.65)',
                color: '#ffffff',
                opacity: 0,
                transition: 'opacity 0.2s',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.85)' },
              }}
            >
              <CloseIcon sx={{ fontSize: 12 }} />
            </IconButton>
          </Box>
        ))}

        {/* Camera upload slot */}
        {extraFiles.length < 3 && (
          <Box
            component="label"
            sx={{
              width: 80,
              height: 64,
              borderRadius: '8px',
              border: '1px dashed #cbd5e1',
              display: 'grid',
              placeItems: 'center',
              color: '#6b7280',
              cursor: disabled ? 'default' : 'pointer',
              transition: 'all 0.2s',
              '&:hover': disabled
                ? undefined
                : {
                    borderColor: '#0F9A00',
                    color: '#0F9A00',
                    backgroundColor: '#EFFFF3',
                  },
            }}
          >
            <CameraAltIcon sx={{ fontSize: 22 }} />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleFileChange}
              style={{ display: 'none' }}
              disabled={disabled}
            />
          </Box>
        )}
      </Box>

      <Typography
        variant="caption"
        sx={{ color: '#6b7280', fontSize: '0.72rem', display: 'block', mt: 0.75, lineHeight: 1.35 }}
      >
        A screenshot of this page is attached automatically — remove it if it shows anything private. You can add images
        or a short video (up to 3 files, 5 MB each).
      </Typography>
    </Box>
  );
};
